"use client";

import { useCallback, useRef, useState } from "react";
import { useSettings } from "./settings-context";
import { useLanguage } from "./language-context";
import api, { type LocaleCode } from "./api/client";
import { getCacheKey, getCachedAudio, cacheAudio } from "./audio-cache";

interface UseTTSOptions {
  lang?: LocaleCode; // Override language code (BCP 47 format like "pt-PT")
}

export type TTSState = "idle" | "loading" | "playing";

// Fixed slow speed for second tap
const SLOW_SPEED = 0.6;

export function useTTS(options: UseTTSOptions = {}) {
  const { targetLanguage } = useLanguage();
  // Use provided lang option, or fall back to target language from context
  const lang = options.lang || targetLanguage.code;
  const { tts } = useSettings();
  const [state, setState] = useState<TTSState>("idle");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track last played text and whether it was slow (for toggle behavior)
  const lastPlayedRef = useRef<{ text: string; wasSlow: boolean } | null>(null);

  // Derived states for backward compatibility
  const isPlaying = state === "playing";
  const isLoading = state === "loading";

  const speakWithBrowser = useCallback(
    (text: string, speed?: number) => {
      if ("speechSynthesis" in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        utterance.rate = speed ?? tts.speed;

        utterance.onstart = () => setState("playing");
        utterance.onend = () => setState("idle");
        utterance.onerror = () => setState("idle");

        speechSynthesis.speak(utterance);
      } else {
        setState("idle");
      }
    },
    [lang, tts.speed]
  );

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Also cancel browser TTS if it's playing as fallback
    if ("speechSynthesis" in window) {
      speechSynthesis.cancel();
    }
    setState("idle");
  }, []);

  const playAudioBlob = useCallback(
    async (blob: Blob, fallbackText?: string, fallbackSpeed?: number) => {
      const url = URL.createObjectURL(blob);
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => {
        setState("idle");
        URL.revokeObjectURL(url);
      };
      audioRef.current.onerror = () => {
        setState("idle");
        URL.revokeObjectURL(url);
      };

      try {
        await audioRef.current.play();
        setState("playing");
      } catch (err) {
        URL.revokeObjectURL(url);
        // NotAllowedError means browser blocked autoplay — silently fall back
        if ((err as Error).name === "NotAllowedError" && fallbackText) {
          speakWithBrowser(fallbackText, fallbackSpeed);
        } else {
          setState("idle");
          throw err;
        }
      }
    },
    [speakWithBrowser]
  );

  const speak = useCallback(
    async (text: string) => {
      // Stop any current playback
      stop();

      if (!text.trim()) return;

      // Determine if we should play slow (toggle on second tap of same text)
      const lastPlayed = lastPlayedRef.current;
      const shouldPlaySlow = lastPlayed?.text === text && !lastPlayed.wasSlow;
      const effectiveSpeed = shouldPlaySlow ? SLOW_SPEED : tts.speed;

      // Update tracking for next tap
      lastPlayedRef.current = { text, wasSlow: shouldPlaySlow };

      setState("loading");

      // Check client-side cache first (speed is part of cache key)
      const cacheKey = getCacheKey({
        text,
        speed: effectiveSpeed,
        lang,
      });

      try {
        const cachedBlob = await getCachedAudio(cacheKey);
        if (cachedBlob) {
          // Cache hit - play directly without network request
          await playAudioBlob(cachedBlob, text, effectiveSpeed);
          return;
        }
      } catch {
        // Cache check failed, continue to fetch
      }

      // Cache miss - fetch from API
      try {
        abortControllerRef.current = new AbortController();

        const { response } = await api.POST("/api/tts", {
          body: {
            text,
            speed: effectiveSpeed,
            lang,
          },
          parseAs: "stream",
          signal: abortControllerRef.current.signal,
        });

        if (response.ok) {
          const contentType = response.headers.get("content-type") || "";
          let audioBlob: Blob;

          if (contentType.includes("application/json")) {
            // Server cache hit: response contains signed URL - fetch the audio
            const data = await response.json();
            const audioResponse = await fetch(data.url);
            audioBlob = await audioResponse.blob();
          } else {
            // Server cache miss: response is audio blob directly
            audioBlob = await response.blob();
          }

          // Store in client cache (non-blocking)
          cacheAudio(cacheKey, audioBlob).catch(() => {});

          // Play the audio
          await playAudioBlob(audioBlob, text, effectiveSpeed);
        } else {
          // Silently fall back to browser TTS
          speakWithBrowser(text, effectiveSpeed);
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          // Silently fall back to browser TTS
          speakWithBrowser(text, effectiveSpeed);
        } else {
          setState("idle");
        }
      }
    },
    [tts, lang, stop, speakWithBrowser, playAudioBlob]
  );

  return { speak, stop, isPlaying, isLoading, state };
}
